import { useEffect, useState } from 'react';

/**
 * True when the viewport is at or below `breakpoint` (768px = tablet/phone
 * by default). Used across the site to gate expensive continuous animations
 * (particle canvases, infinite floating/pulsing loops, drag physics) that
 * look great on a laptop/desktop but mostly just burn battery and cause
 * jank on phones and tablets — scroll-triggered one-shot reveals and CSS
 * transitions stay on for every device; this only turns off the "ambient
 * motion" layer.
 *
 * Also respects the OS-level "reduce motion" accessibility setting: if a
 * user has that on, we treat them as mobile for animation purposes
 * regardless of screen size.
 */
export const useIsMobile = (breakpoint = 768): boolean => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    return reduceMotion || window.innerWidth <= breakpoint;
  });

  useEffect(() => {
    const sizeQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setIsMobile(sizeQuery.matches || motionQuery.matches);
    update();
    sizeQuery.addEventListener('change', update);
    motionQuery.addEventListener('change', update);
    return () => {
      sizeQuery.removeEventListener('change', update);
      motionQuery.removeEventListener('change', update);
    };
  }, [breakpoint]);

  return isMobile;
};

export default useIsMobile;
